import { OrderedDocument } from '@/types/doc';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Icon } from '@iconify/react';
import { Button, Card } from '@nextui-org/react';

interface SortableDocumentCardProps {
  orderedDocument: OrderedDocument;
  index: number;
  onRemove: (id: number) => void;
  getFileIcon: (filename: string) => string;
  isRemoving: boolean;
}

export const SortableDocumentCardNew = ({
  orderedDocument,
  index,
  onRemove,
  getFileIcon,
  isRemoving,
}: SortableDocumentCardProps) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: orderedDocument.document.id,
  });

  const style = transform
    ? {
        transform: CSS.Transform.toString(transform),
        transition,
      }
    : undefined;

  return (
    <Card
      ref={setNodeRef}
      className={`p-4 border-1 hover:shadow-md transition-shadow group ${
        isDragging ? 'shadow-lg ring-2 ring-primary bg-transparent' : ''
      }`}
      shadow="none"
      style={style}
    >
      <div
        className={`flex justify-between items-center ${isDragging ? 'opacity-0' : ''}`}
      >
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <div {...attributes} {...listeners}>
              <Icon
                icon="lucide:grip-vertical"
                className="text-default-400 opacity-0 group-hover:opacity-100 transition-opacity cursor-grab"
              />
            </div>
            <Icon
              icon={getFileIcon(orderedDocument.document.filename)}
              className="text-primary text-2xl flex-shrink-0"
            />
          </div>
          <div className="flex-1 min-w-0">
            <p
              className="text-sm font-medium truncate"
              title={orderedDocument.document.filename}
            >
              {index + 1}. {orderedDocument.document.filename}
            </p>
            <p className="text-xs text-default-500">
              Batch ID: {orderedDocument.document.batch_id}
            </p>
          </div>
        </div>
        <Button
          isIconOnly
          size="sm"
          variant="light"
          className="opacity-0 group-hover:opacity-100 transition-opacity"
          onPress={() => onRemove(orderedDocument.id)}
          isLoading={isRemoving}
        >
          <Icon icon="lucide:trash-2" className="text-default-600 w-4 h-4" />
        </Button>
      </div>
    </Card>
  );
};
